export type ZkillStats = {
  dangerRatio: number;
  gangRatio: number;
  shipsDestroyed: number;
  shipsLost: number;
};

export type ResolvedCharacter = {
  characterId: number;
  characterName: string;
  corp: string;
  corpId: number;
  corpTicker: string;
  alliance: string | null;
  allianceId: number | null;
  allianceTicker: string | null;
};

type ResolveResponse = {
  data: {
    characterId: number;
    name: string;
    corpId: number;
    corpName: string;
    corpTicker: string;
    allianceId: number | null;
    allianceName: string | null;
    allianceTicker: string | null;
  } | null;
};

// Session-level deduplication
const resolveCache = new Map<string, ResolvedCharacter>();
const zkillStatsMap = reactive(new Map<number, ZkillStats | null>());
const pendingResolves = new Map<string, Promise<ResolvedCharacter | null>>();
const pendingZkill = new Set<number>();
const zkillFailCount = new Map<number, number>(); // retry tracking
const MAX_ZKILL_RETRIES = 2;

function portraitUrl(characterId: number, size: 32 | 64 | 128 | 256 = 32): string {
  return `https://images.evetech.net/characters/${characterId}/portrait?size=${size}`;
}

function zkillUrl(characterId: number): string {
  return `https://zkillboard.com/character/${characterId}/`;
}

function zkillCorpUrl(corpId: number): string {
  return `https://zkillboard.com/corporation/${corpId}/`;
}

function zkillAllianceUrl(allianceId: number): string {
  return `https://zkillboard.com/alliance/${allianceId}/`;
}

export function useESI() {
  async function resolveCharacter(name: string): Promise<ResolvedCharacter | null> {
    if (!name.trim()) return null;

    const key = name.toLowerCase();
    const cached = resolveCache.get(key);
    if (cached) return cached;

    const pending = pendingResolves.get(key);
    if (pending) return pending;

    const promise = (async () => {
      try {
        const res = await $fetch<ResolveResponse>('/api/esi/characters/resolve', {
          method: 'POST',
          body: { name },
        });

        if (!res.data) return null;

        const resolved: ResolvedCharacter = {
          characterId: res.data.characterId,
          characterName: res.data.name,
          corp: res.data.corpName,
          corpId: res.data.corpId,
          corpTicker: res.data.corpTicker,
          alliance: res.data.allianceName,
          allianceId: res.data.allianceId,
          allianceTicker: res.data.allianceTicker,
        };
        resolveCache.set(key, resolved);
        return resolved;
      } catch {
        return null;
      } finally {
        pendingResolves.delete(key);
      }
    })();

    pendingResolves.set(key, promise);
    return promise;
  }

  function loadZkillStats(characterId: number): void {
    if (zkillStatsMap.has(characterId) || pendingZkill.has(characterId)) return;
    if ((zkillFailCount.get(characterId) ?? 0) >= MAX_ZKILL_RETRIES) return;
    pendingZkill.add(characterId);

    $fetch<{ data: ZkillStats | null }>(`/api/esi/zkill/characters/${characterId}`)
      .then((res) => {
        zkillStatsMap.set(characterId, res.data);
      })
      .catch(() => {
        // Don't cache failures — allow retry on next render (up to MAX_ZKILL_RETRIES)
        zkillFailCount.set(characterId, (zkillFailCount.get(characterId) ?? 0) + 1);
      })
      .finally(() => {
        pendingZkill.delete(characterId);
      });
  }

  return {
    resolveCharacter,
    zkillStatsMap,
    loadZkillStats,
    portraitUrl,
    zkillUrl,
    zkillCorpUrl,
    zkillAllianceUrl,
  };
}
